"use client";

import React, { useEffect, useState } from "react";
import { Search, Map, Rocket, TrendingUp, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: 1,
  title: "Discovery",
  description: "Deep-dive analysis of your enterprise ecosystem to identify high-impact AI and Salesforce integration vectors.",
  icon: Search
}, {
  id: 2,
  title: "Architecture",
  description: "Engineering robust API layers and secure computing infrastructure designed for the Apple Business environment.",
  icon: Map
}, {
  id: 3,
  title: "Integration",
  description: "Seamless deployment of custom ISV and OEM solutions, bridging advanced AI compute with sovereign gateways.",
  icon: Rocket
}, {
  id: 4,
  title: "Sovereign Launch",
  description: "Activating scalable digital ecosystems where autonomous agents and stablecoin rewards power future growth.",
  icon: TrendingUp
}];
export default function OurProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const [activeStep, setActiveStep] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setActiveStep(prev => (prev + 1) % steps.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [isVisible]);
  return <section data-section-id="57775" id="applied-innovation" ref={ref} className="relative w-full py-24 overflow-hidden dark bg-background text-foreground">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-3xl md:text-5xl font-default font-bold tracking-tighter mb-4">
            Applied Innovation
          </h2>
          <p className="text-lg font-default text-muted-foreground">
            A precision-engineered journey from foundational strategy to sovereign digital infrastructure.
            We build the systems that power the future of intelligent business.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-muted -z-10">
            <div className={`h-full bg-gradient-to-r from-primary via-primary to-secondary transition-all duration-1000 ease-out`} style={{
            width: isVisible ? "100%" : "0%"
          }} />
          </div>

          {}
          <div className="md:hidden absolute top-0 left-8 bottom-0 w-0.5 bg-muted -z-10">
             <div className={`w-full bg-gradient-to-b from-primary via-primary to-secondary transition-all duration-1000 ease-out`} style={{
            height: isVisible ? "100%" : "0%"
          }} />
          </div>

          {}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
            {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = activeStep === index;
            const delay = index * 200;
            return <div key={step.id} data-index={index} className={`relative flex flex-col md:items-center md:text-center group transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
              transitionDelay: `${delay}ms`
            }} onMouseEnter={() => setActiveStep(index)}>
                  {}
                  <div className="flex items-center justify-center mb-6 relative">
                    <div className={`w-16 h-16 md:w-24 md:h-24 rounded-full flex items-center justify-center border-4 transition-all duration-500 z-10 bg-card ${isActive ? "border-primary shadow-[0_0_30px_-5px_var(--color-primary)] scale-110" : "border-muted group-hover:border-primary/50"}`}>
                      <Icon className={`w-8 h-8 md:w-10 md:h-10 transition-colors duration-300 ${isActive ? "text-primary animate-pulse" : "text-muted-foreground"}`} />
                    </div>
                    
                    {}
                    <div className={`absolute -top-2 -right-2 md:top-0 md:right-0 bg-card border border-border text-xs font-mono font-bold px-2 py-1 rounded-full shadow-sm transition-colors duration-300 ${isActive ? "text-primary border-primary" : "text-muted-foreground"}`}>
                      0{step.id}
                    </div>
                  </div>

                  {}
                  <div className="pl-20 md:pl-0">
                    <h3 className={`text-xl md:text-2xl font-default font-bold mb-3 transition-colors duration-300 ${isActive ? "text-primary" : "text-foreground"}`}>
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground font-default leading-relaxed text-sm md:text-base">
                      {step.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div className={`mt-20 text-center transition-all duration-700 delay-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Button size="lg" className="rounded-none px-8 h-12 text-base font-default shadow-xl hover:shadow-primary/25 bg-primary text-primary-foreground hover:bg-primary/90" asChild>
            <Link to="#">
              Engineer Your Solution <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}